import { useMemo, useState, useEffect } from "react";

import { useSearch as useSearchApi } from "@/api/generated";
import { SearchResult, UserRole } from "@/api/generated.schemas";
import { NavigationItem } from "@/utils/navigationSearchItems";
import { useNavigationSearch } from "./useNavigationSearch";

/**
 * Debounce hook for search input.
 */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Combined search result type.
 * Includes both navigation items (client-side) and API search results.
 */
export interface CombinedSearchResult {
  navigationResults: NavigationItem[];
  apiResults: SearchResult[];
  total: number;
  categories: Record<string, number>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for unified search across navigation and API.
 * Combines instant client-side navigation search with debounced API search.
 *
 * @param query - Search query string
 * @param userRole - Current user's role for navigation filtering
 * @param enabled - Whether to enable the search (default: true)
 * @returns Combined search results from navigation and API
 */
export function useCombinedSearch(
  query: string,
  userRole: UserRole,
  enabled: boolean = true
): CombinedSearchResult {
  // Debounce the query for API calls (300ms)
  const debouncedQuery = useDebouncedValue(query, 300);

  // Client-side navigation search (instant)
  const navigationResults = useNavigationSearch(query, userRole);

  // API search (debounced)
  const shouldFetchApi = enabled && debouncedQuery.length >= 2;

  const {
    data: apiData,
    isLoading,
    error,
  } = useSearchApi(
    { q: debouncedQuery },
    {
      query: {
        enabled: shouldFetchApi,
        staleTime: 30000, // Cache for 30 seconds
        retry: 1,
      },
    }
  );

  // Check if we're still debouncing (query changed but debounced hasn't caught up)
  const isDebouncing = query !== debouncedQuery && query.length >= 2;

  // Combine results
  return useMemo(() => {
    // Don't show stale API results while debouncing a new query
    const apiResults = isDebouncing ? [] : (apiData?.results ?? []);
    const categories = isDebouncing ? {} : (apiData?.categories ?? {});

    // Add navigation count to categories
    const combinedCategories = { ...categories };
    if (navigationResults.length > 0) {
      combinedCategories.navigation = navigationResults.length;
    }

    // Calculate total
    const total =
      navigationResults.length + (isDebouncing ? 0 : (apiData?.total ?? apiResults.length));

    return {
      navigationResults,
      apiResults,
      total,
      categories: combinedCategories,
      isLoading: (shouldFetchApi && isLoading) || isDebouncing,
      error: error as Error | null,
    };
  }, [navigationResults, apiData, isLoading, error, shouldFetchApi, isDebouncing]);
}
